import { type MessageId, type ThreadId } from "@cmmd-forge/contracts";
import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { type ChatMessage, type Thread } from "../../types";
import { type DraftId } from "../../draftId";
import {
  buildTimelineMessagesWithPreviewHandoffs,
  collectUserMessageBlobPreviewUrls,
  revokeUserMessagePreviewUrls,
} from "../ChatView.logic";
import { useAttachmentPreviewHandoffs } from "./useAttachmentPreviewHandoffs";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import type { StagedComposerSubmission } from "../../composerStagedSubmission";
import { stagedComposerSubmissionToChatMessage } from "./stagedComposerSubmissionMessage";

export function useChatTimelineMessages(input: {
  readonly activeThread: Thread | undefined;
  readonly draftId: DraftId | null | undefined;
  readonly threadId: ThreadId;
  readonly stagedSubmission: StagedComposerSubmission | null;
  readonly resetLocalDispatch: () => void;
  readonly setExpandedImage: Dispatch<SetStateAction<ExpandedImagePreview | null>>;
}): {
  readonly optimisticUserMessages: ChatMessage[];
  readonly setOptimisticUserMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  readonly optimisticUserMessagesRef: { current: ChatMessage[] };
  readonly serverMessages: Thread["messages"] | undefined;
  readonly timelineMessages: ChatMessage[];
  readonly clearAttachmentPreviewHandoffs: () => void;
  readonly handoffAttachmentPreviews: (messageId: MessageId, previewUrls: string[]) => void;
} {
  const {
    activeThread,
    draftId,
    resetLocalDispatch,
    setExpandedImage,
    stagedSubmission,
    threadId,
  } = input;
  const [optimisticUserMessages, setOptimisticUserMessages] = useState<ChatMessage[]>([]);
  const optimisticUserMessagesRef = useRef(optimisticUserMessages);
  optimisticUserMessagesRef.current = optimisticUserMessages;

  const serverMessages = activeThread?.messages;
  const {
    attachmentPreviewHandoffByMessageId,
    clearAttachmentPreviewHandoffs,
    handoffAttachmentPreviews,
  } = useAttachmentPreviewHandoffs({
    serverMessages,
    optimisticUserMessagesRef,
  });

  const timelineMessages = useMemo(() => {
    const stagedMessage = stagedComposerSubmissionToChatMessage(stagedSubmission);
    return buildTimelineMessagesWithPreviewHandoffs({
      serverMessages,
      attachmentPreviewHandoffByMessageId,
      optimisticUserMessages:
        stagedMessage && !optimisticUserMessages.some((message) => message.id === stagedMessage.id)
          ? [...optimisticUserMessages, stagedMessage]
          : optimisticUserMessages,
    });
  }, [
    serverMessages,
    attachmentPreviewHandoffByMessageId,
    optimisticUserMessages,
    stagedSubmission,
  ]);

  useEffect(() => {
    if (!activeThread?.id) return;
    if (activeThread.messages.length === 0) {
      return;
    }
    const serverIds = new Set(activeThread.messages.map((message) => message.id));
    const removedMessages = optimisticUserMessages.filter((message) => serverIds.has(message.id));
    if (removedMessages.length === 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setOptimisticUserMessages((existing) =>
        existing.filter((message) => !serverIds.has(message.id)),
      );
    }, 0);
    for (const removedMessage of removedMessages) {
      const previewUrls = collectUserMessageBlobPreviewUrls(removedMessage);
      if (previewUrls.length > 0) {
        handoffAttachmentPreviews(removedMessage.id, previewUrls);
        continue;
      }
      revokeUserMessagePreviewUrls(removedMessage);
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [handoffAttachmentPreviews, activeThread?.id, activeThread?.messages, optimisticUserMessages]);

  useEffect(() => {
    setOptimisticUserMessages((existing) => {
      // Bail when there is nothing to clear. Returning a fresh [] unconditionally
      // changes the state reference on every run, so an unmemoised callback in
      // this effect's deps closes a render loop and React throws #185.
      if (existing.length === 0) {
        return existing;
      }
      for (const message of existing) {
        revokeUserMessagePreviewUrls(message);
      }
      return [];
    });
    resetLocalDispatch();
    setExpandedImage(null);
  }, [draftId, resetLocalDispatch, setExpandedImage, threadId]);

  return {
    optimisticUserMessages,
    setOptimisticUserMessages,
    optimisticUserMessagesRef,
    serverMessages,
    timelineMessages,
    clearAttachmentPreviewHandoffs,
    handoffAttachmentPreviews,
  };
}
